import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Bell, ShieldAlert, X, CheckCheck } from 'lucide-react';

const levelColor = {
  info: 'border-blue-200 bg-blue-50',
  warning: 'border-amber-200 bg-amber-50',
  critical: 'border-red-200 bg-red-50',
};

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

export const SecurityBell = () => {
  const API_URL = process.env.REACT_APP_API_URL;
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  const fetch = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/superadmin/notifications?limit=10`);
      setItems(data.items || []);
      setUnreadCount(data.unread_count || 0);
    } catch { /* empty */ }
  }, [API_URL]);

  useEffect(() => {
    fetch();
    const t = setInterval(fetch, 60000);
    return () => clearInterval(t);
  }, [fetch]);

  // Calculate panel position when opening (viewport-aware)
  useEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const panelWidth = Math.min(416, window.innerWidth - 16); // 26rem max, min 16px margin
    let left = rect.right + 8; // default: open to the right of the bell
    // If no room on right, open to the left of the bell
    if (left + panelWidth > window.innerWidth - 8) {
      left = Math.max(8, rect.left - panelWidth - 8);
    }
    // If still overflowing (very narrow screens), clamp to viewport
    if (left + panelWidth > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - panelWidth - 8);
    }
    const top = Math.min(rect.top, window.innerHeight - 500); // ensure panel fits vertically
    setPanelPos({ top: Math.max(8, top), left });
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) &&
          btnRef.current && !btnRef.current.contains(e.target)) setOpen(false);
    };
    const handleResize = () => { if (open) setOpen(false); };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('resize', handleResize);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
    };
  }, [open]);

  const markRead = async (id) => {
    try {
      await axios.post(`${API_URL}/api/superadmin/notifications/${id}/read`);
      fetch();
    } catch { /* empty */ }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API_URL}/api/superadmin/notifications/mark-all-read`);
      fetch();
    } catch { /* empty */ }
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
        data-testid="security-bell"
        aria-label="Güvenlik Uyarıları"
      >
        <Bell className={`w-5 h-5 ${unreadCount > 0 ? 'text-amber-400' : 'text-white/70'}`} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center font-bold" data-testid="security-bell-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          style={{ top: `${panelPos.top}px`, left: `${panelPos.left}px`, width: `${Math.min(416, window.innerWidth - 16)}px` }}
          className="fixed bg-white rounded-xl border border-[#E5E5E5] shadow-2xl z-[100] overflow-hidden"
          data-testid="security-bell-panel"
        >
          <div className="flex items-center justify-between p-3 border-b border-[#E5E5E5] bg-[#F9F8F6]">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-[#2563EB]" />
              <span className="text-sm font-semibold text-[#1A1A2E]">Güvenlik Uyarıları</span>
              {unreadCount > 0 && <span className="text-xs bg-red-100 text-red-700 px-1.5 rounded-full">{unreadCount}</span>}
            </div>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-xs text-[#2563EB] hover:underline flex items-center gap-1" data-testid="mark-all-read">
                <CheckCheck className="w-3 h-3" /> Tümünü oku
              </button>
            )}
          </div>

          <div className="max-h-[480px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-6 text-center text-[#999] text-sm">
                <Bell className="w-8 h-8 mx-auto mb-2" strokeWidth={1.5} />
                Henüz uyarı yok
              </div>
            ) : items.map((n) => (
              <div
                key={n.id}
                className={`p-3 border-b border-[#E5E5E5] last:border-b-0 ${n.read ? 'bg-white' : levelColor[n.level] || levelColor.warning}`}
                data-testid={`security-notif-${n.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2 mb-1">
                      <span className="text-sm font-semibold text-[#1A1A2E] break-words leading-snug flex-1">{n.title}</span>
                      {!n.read && <span className="w-2 h-2 rounded-full bg-red-500 shrink-0 mt-1.5" />}
                    </div>
                    <p className="text-xs text-[#595959] whitespace-pre-line break-words leading-relaxed">{n.message}</p>
                    {n.extra?.location && (
                      <p className="text-[11px] text-[#595959] mt-1.5 font-medium break-words" data-testid={`notif-geo-${n.id}`}>
                        📍 {n.extra.location}
                      </p>
                    )}
                    <p className="text-[10px] text-[#999] mt-1.5">{fmtDate(n.created_at)}</p>
                  </div>
                  {!n.read && (
                    <button onClick={() => markRead(n.id)} className="shrink-0 p-1 hover:bg-white/50 rounded" title="Okundu" data-testid={`mark-read-${n.id}`}>
                      <X className="w-3.5 h-3.5 text-[#595959]" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};
